/**
 * TextReveal — Reusable letter-by-letter & word-rise animation for Shopify
 * =========================================================================
 *
 * ── CLASS 1: text-reveal ─────────────────────────────────────────────────
 *   Letter-by-letter or word-by-word reveal.
 *
 *   <h1 class="text-reveal" data-mode="fade-up">Craft. Curated. Yours.</h1>
 *   <h1 class="text-reveal" data-mode="word-rise">Foundations of Flavor</h1>
 *
 *   data-mode options:
 *     fade-up      — letters rise up and fade in (default)
 *     fade-in      — letters fade in only
 *     slide-down   — letters drop down and fade in
 *     blur         — letters unblur and fade in
 *     scramble     — letters scramble then resolve
 *     word-rise    — whole words rise from below with rotation (like Soche heading)
 *
 *   data-split    : letter (default) | word  [ignored for word-rise, always word]
 *   data-delay    : ms before animation starts (default: 0)
 *   data-duration : animation duration per unit in ms (default: 750 for word-rise, 500 others)
 *   data-stagger  : ms between each word/letter (default: 90 for word-rise, 40 others)
 *   data-once     : true (default) | false — re-animate on each scroll entry
 *
 * ── CLASS 2: fade-up-element ─────────────────────────────────────────────
 *   Whole-element fade + slide-up. Works on any element.
 *
 *   <div class="fade-up-element">...</div>
 *   <div class="fade-up-element" data-delay="150" data-duration="800">...</div>
 *
 *   data-delay     : ms before animation (default: 0)
 *   data-duration  : transition duration ms (default: 600)
 *   data-distance  : translateY distance px (default: 40)
 *   data-once      : true (default) | false
 *
 * ── Trigger ──────────────────────────────────────────────────────────────
 *   Animations fire when the element reaches 70% from the top of the viewport.
 *   To change: find OBSERVER_OPTIONS and adjust rootMargin (e.g. '-50% 0px 0px 0px')
 *
 * ── Install ──────────────────────────────────────────────────────────────
 *   Theme Editor → Assets → upload as text-reveal-animation.js
 *   In theme.liquid before </body>:
 *     {{ 'text-reveal-animation.js' | asset_url | script_tag }}
 *
 * ── Manual JS ────────────────────────────────────────────────────────────
 *   const r = new TextReveal('.my-heading', { mode: 'word-rise' });
 *   const f = new FadeUpElement('.my-cards', { duration: 800 });
 *   r.destroy(); f.destroy();
 */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════
     OBSERVER — fires at 70% from top of viewport
     Change -70% to adjust: -50% = halfway, -30% = earlier
  ═══════════════════════════════════════════════════════════════════ */
  const OBSERVER_OPTIONS = {
    rootMargin: '90px 0px 0px 0px',
    threshold: 0,
  };

  /* ═══════════════════════════════════════════════════════════════════
     CSS
  ═══════════════════════════════════════════════════════════════════ */
  const STYLES = `
    /* ── Base spans ── */
    .tr-char, .tr-word {
      display: inline-block;
      white-space: pre;
      will-change: opacity, transform;
    }
    .tr-splitting { visibility: hidden; }

    /* ── Keyframes: letter modes ── */
    @keyframes tr-fade-up {
      from { opacity: 0; transform: translateY(0.5em); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes tr-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes tr-slide-down {
      from { opacity: 0; transform: translateY(-0.5em); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes tr-blur-in {
      from { opacity: 0; filter: blur(8px); transform: scale(1.1); }
      to   { opacity: 1; filter: blur(0);   transform: scale(1); }
    }

    /* ── Keyframe: word-rise (matches Soche section heading) ── */
    @keyframes tr-word-rise {
      0%   { transform: translateY(110%) rotate(-4deg) translateX(-8px); opacity: 0; }
      30%  { opacity: 1; }
      100% { transform: translateY(0%) rotate(0deg) translateX(0px);    opacity: 1; }
    }

    /* ── Hidden before animate class ── */
    .tr-char, .tr-word { opacity: 0; }

    /* ── Letter modes: activate on .tr-animate ── */
    .tr-animate[data-mode="fade-up"]    .tr-char,
    .tr-animate[data-mode="fade-up"]    .tr-word,
    .tr-animate:not([data-mode])        .tr-char {
      animation: tr-fade-up var(--tr-dur, 0.5s) cubic-bezier(0.16, 1, 0.3, 1) both;
      animation-delay: var(--tr-delay, 0ms);
    }
    .tr-animate[data-mode="fade-in"] .tr-char,
    .tr-animate[data-mode="fade-in"] .tr-word {
      animation: tr-fade-in var(--tr-dur, 0.5s) ease both;
      animation-delay: var(--tr-delay, 0ms);
    }
    .tr-animate[data-mode="slide-down"] .tr-char,
    .tr-animate[data-mode="slide-down"] .tr-word {
      animation: tr-slide-down var(--tr-dur, 0.5s) cubic-bezier(0.16, 1, 0.3, 1) both;
      animation-delay: var(--tr-delay, 0ms);
    }
    .tr-animate[data-mode="blur"] .tr-char,
    .tr-animate[data-mode="blur"] .tr-word {
      animation: tr-blur-in var(--tr-dur, 0.6s) cubic-bezier(0.16, 1, 0.3, 1) both;
      animation-delay: var(--tr-delay, 0ms);
    }
    .tr-animate[data-mode="scramble"] .tr-char { opacity: 1; }

    /* ── word-rise mode ──
       Each word needs overflow:hidden on its wrapper so the rising word
       is clipped during the translateY travel — exactly like the Soche effect.
       The .tr-word-wrap clips; the .tr-word-inner is what animates.
    ── */
  .tr-word-wrap {
  display: inline-block;
  overflow: hidden;
  vertical-align: bottom;
  margin: 0;
  padding-bottom: 0.2em;
  margin-bottom: -0.2em;
}
    .tr-word-inner {
      display: inline-block;
      opacity: 0;
      transform: translateY(110%) rotate(-4deg) translateX(-8px);
      transform-origin: left bottom;
      will-change: transform, opacity;
    }
    .tr-animate[data-mode="word-rise"] .tr-word-inner {
      animation: tr-word-rise var(--tr-dur, 0.75s) cubic-bezier(0.22, 0.8, 0.36, 1) both;
      animation-delay: var(--tr-delay, 0ms);
    }

    /* ── fade-up-element ── */
 .fade-up-element {
  opacity: 0;
  transform: translateY(var(--fue-distance, 50px));
  will-change: opacity, transform;
  transition:
    opacity var(--fue-dur, 0.9s) cubic-bezier(0.22, 0.6, 0.36, 1),
    transform var(--fue-dur, 0.9s) cubic-bezier(0.22, 0.6, 0.36, 1);
  transition-delay: var(--fue-delay, 0ms);
}
.fade-up-element.fue-animate {
  opacity: 1;
  transform: translateY(0);
}

    /* ── Reduced motion ── */
    @media (prefers-reduced-motion: reduce) {
      .tr-char, .tr-word, .tr-word-inner, .fade-up-element {
        animation: none !important;
        opacity: 1 !important;
        transform: none !important;
        filter: none !important;
      }
    }
  `;

  function injectStyles() {
    if (document.getElementById('tr-global-styles')) return;
    const s = document.createElement('style');
    s.id = 'tr-global-styles';
    s.textContent = STYLES;
    document.head.appendChild(s);
  }

  /* ═══════════════════════════════════════════════════════════════════
     HELPERS
  ═══════════════════════════════════════════════════════════════════ */
  const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';

  function scrambleLetter(el, targetChar, durationMs) {
    if (targetChar === '\u00A0' || targetChar === ' ') return;
    const totalFrames = Math.max(6, Math.round(durationMs / 16));
    let frame = 0;
    function tick() {
      frame++;
      el.textContent = frame < totalFrames
        ? SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)]
        : targetChar;
      if (frame < totalFrames) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  function resolveEls(target) {
    if (typeof target === 'string') return Array.from(document.querySelectorAll(target));
    if (target instanceof Element) return [target];
    if (target instanceof NodeList || Array.isArray(target)) return Array.from(target);
    return [];
  }

  function getOpt(el, attr, fallback) {
    const val = el.dataset[attr];
    if (val === undefined || val === '') return fallback;
    if (val === 'true') return true;
    if (val === 'false') return false;
    const n = Number(val);
    return isNaN(n) ? val : n;
  }

  /* ═══════════════════════════════════════════════════════════════════
     CLASS 1 — TextReveal
  ═══════════════════════════════════════════════════════════════════ */
  class TextReveal {
    constructor(target = '.text-reveal', options = {}) {
      injectStyles();
      this._opts = Object.assign(
        { delay: 0, duration: 500, stagger: 40, mode: 'fade-up', split: 'letter', once: true },
        options
      );
      this._observers = [];
      this._els = resolveEls(target);
      this._init();
    }

    _splitElement(el) {
      const mode    = getOpt(el, 'mode',     this._opts.mode);
      const split   = getOpt(el, 'split',    this._opts.split);
      const delay   = getOpt(el, 'delay',    this._opts.delay);
      const stagger = getOpt(el, 'stagger',  mode === 'word-rise' ? 90 : this._opts.stagger);
      const dur     = getOpt(el, 'duration', mode === 'word-rise' ? 750 : this._opts.duration);

      el.setAttribute('data-mode', mode);
      el.style.setProperty('--tr-dur', `${dur}ms`);

      const originalText = el.textContent;
      el.setAttribute('aria-label', originalText);
      el.classList.add('tr-splitting');

      const fragment = document.createDocumentFragment();

      if (mode === 'word-rise') {
        /* ── word-rise: wrap each word in overflow:hidden clip ── */
        const words = originalText.split(/(\s+)/);
        let wordIndex = 0;
        words.forEach((chunk) => {
          if (/^\s+$/.test(chunk)) {
            /* preserve spaces between words as plain text */
            fragment.appendChild(document.createTextNode(' '));
          } else {
            const wrap  = document.createElement('span');
            const inner = document.createElement('span');
            wrap.className  = 'tr-word-wrap';
            inner.className = 'tr-word-inner';
            inner.setAttribute('aria-hidden', 'true');
            inner.textContent = chunk;
            /* bake delay per word */
            inner.style.setProperty('--tr-delay', `${delay + wordIndex * stagger}ms`);
            wordIndex++;
            wrap.appendChild(inner);
            fragment.appendChild(wrap);
          }
        });

      } else if (split === 'word') {
        let wordIndex = 0;
        originalText.split(/(\s+)/).forEach((chunk) => {
          if (/^\s+$/.test(chunk)) {
            fragment.appendChild(document.createTextNode(chunk));
          } else {
            const s = document.createElement('span');
            s.classList.add('tr-word');
            s.setAttribute('aria-hidden', 'true');
            s.textContent = chunk;
            s.style.setProperty('--tr-delay', `${delay + wordIndex * stagger}ms`);
            wordIndex++;
            fragment.appendChild(s);
          }
        });

      } else {
        /* letter split */
        let charIndex = 0;
        [...originalText].forEach((char) => {
          const s = document.createElement('span');
          s.classList.add('tr-char');
          s.setAttribute('aria-hidden', 'true');
          s.textContent = char === ' ' ? '\u00A0' : char;
          if (char === ' ') s.style.minWidth = '0.3em';
          s.style.setProperty('--tr-delay', `${delay + charIndex * stagger}ms`);
          charIndex++;
          fragment.appendChild(s);
        });
      }

      el.textContent = '';
      el.appendChild(fragment);
      el.classList.remove('tr-splitting');
      el._trSplit = true;
      el._trMode  = mode;
    }

    _animateElement(el) {
      const mode = el._trMode || getOpt(el, 'mode', this._opts.mode);

      if (mode === 'scramble') {
        const delay   = getOpt(el, 'delay',    this._opts.delay);
        const stagger = getOpt(el, 'stagger',  this._opts.stagger);
        const dur     = getOpt(el, 'duration', this._opts.duration);
        el.classList.add('tr-animate');
        Array.from(el.querySelectorAll('.tr-char')).forEach((unit, i) => {
          setTimeout(() => scrambleLetter(unit, unit.textContent, dur), delay + i * stagger);
        });
      } else {
        requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('tr-animate')));
      }
    }

    _resetElement(el) {
      el.classList.remove('tr-animate');
      void el.offsetWidth; /* force reflow so animation re-fires */
    }

    _observe(el) {
      const once = getOpt(el, 'once', this._opts.once);
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._animateElement(el);
            if (once) observer.unobserve(el);
          } else if (!once) {
            this._resetElement(el);
          }
        });
      }, OBSERVER_OPTIONS);

      observer.observe(el);
      this._observers.push(observer);
    }

    _init() {
      this._els.forEach((el) => {
        if (!el._trSplit) this._splitElement(el);
        this._observe(el);
      });
    }

    play()    { this._els.forEach((el) => { this._resetElement(el); this._animateElement(el); }); }
    reset()   { this._els.forEach((el) => this._resetElement(el)); }
    destroy() { this._observers.forEach((o) => o.disconnect()); this._observers = []; }
  }

  /* ═══════════════════════════════════════════════════════════════════
     CLASS 2 — FadeUpElement
  ═══════════════════════════════════════════════════════════════════ */
  class FadeUpElement {
    constructor(target = '.fade-up-element', options = {}) {
      injectStyles();
      this._opts = Object.assign(
        { delay: 0, duration: 600, distance: 40, once: true },
        options
      );
      this._observers = [];
      this._els = resolveEls(target);
      this._init();
    }

    _prepareElement(el) {
      el.style.setProperty('--fue-dur',      `${getOpt(el, 'duration', this._opts.duration)}ms`);
      el.style.setProperty('--fue-distance', `${getOpt(el, 'distance', this._opts.distance)}px`);
      el.style.setProperty('--fue-delay',    `${getOpt(el, 'delay',    this._opts.delay)}ms`);
    }

    _animateElement(el) {
      requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('fue-animate')));
    }

    _resetElement(el) {
      el.classList.remove('fue-animate');
      void el.offsetWidth;
    }

    _observe(el) {
      const once = getOpt(el, 'once', this._opts.once);
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._animateElement(el);
            if (once) observer.unobserve(el);
          } else if (!once) {
            this._resetElement(el);
          }
        });
      }, OBSERVER_OPTIONS);

      observer.observe(el);
      this._observers.push(observer);
    }

    _init() {
      this._els.forEach((el) => {
        this._prepareElement(el);
        this._observe(el);
      });
    }

    play()    { this._els.forEach((el) => { this._resetElement(el); this._animateElement(el); }); }
    reset()   { this._els.forEach((el) => this._resetElement(el)); }
    destroy() { this._observers.forEach((o) => o.disconnect()); this._observers = []; }
  }

  /* ═══════════════════════════════════════════════════════════════════
     AUTO-INIT
  ═══════════════════════════════════════════════════════════════════ */
  function autoInit() {
    if (document.querySelectorAll('.text-reveal').length) {
      window._textRevealInstance = new TextReveal('.text-reveal');
    }
    if (document.querySelectorAll('.fade-up-element').length) {
      window._fadeUpElementInstance = new FadeUpElement('.fade-up-element');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }

  /* Shopify Theme Editor live preview */
  document.addEventListener('shopify:section:load', () => {
    if (window._textRevealInstance)    window._textRevealInstance.destroy();
    if (window._fadeUpElementInstance) window._fadeUpElementInstance.destroy();
    autoInit();
  });

  window.TextReveal    = TextReveal;
  window.FadeUpElement = FadeUpElement;

})();